import { Component, Input, Output, EventEmitter, HostListener, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';

export interface NavItem {
  label: string;
  icon: string;
  route: string;
}

import { SidebarStateService } from '../../../../services/sidebar-state.service';
import { AuthService, TenantMembership } from '../../../auth/auth.service';
import { Company, CompanyService } from '../../../../services/apis/company/company.service';
import { CompanyContextService } from '../../../../services/company-context.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, TooltipModule],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit {
  readonly sidebarService = inject(SidebarStateService);
  readonly auth = inject(AuthService);
  private readonly companyService = inject(CompanyService);
  private readonly companyContext = inject(CompanyContextService);

  @Input() navItems: NavItem[] = [];
  @Input() companyName = '';
  @Input() companyId = '';
  @Input() version = '';
  @Input() userInitials = '';
  @Input() userName = '';
  @Input() userRole = '';
  @Input() collapsed = false;

  @Output() logout = new EventEmitter<void>();

  companies: Company[] = [];
  tenants: TenantMembership[] = [];
  selectedCompanyId: number | null = null;
  organisationMenuOpen = false;
  switchingOrganisation = false;

  /** Company name resolved from /companies/me for the logged-in user. */
  resolvedCompanyName = '';
  resolvedCompanyId: number | null = null;

  /** Sub-line under the WorkWell logo — the live company name, falling back to the input. */
  get companySubtitle(): string {
    return this.resolvedCompanyName || this.companyId;
  }

  get isSuperAdmin(): boolean {
    return this.auth.session()?.user.isSuperAdmin ?? false;
  }

  get selectedCompanyName(): string {
    const activeTenant = this.tenants.find(tenant => this.tenantId(tenant) === this.auth.session()?.user.tenantId);
    if (activeTenant) return this.tenantName(activeTenant);
    return this.companies.find(company => company.compId === this.selectedCompanyId)?.name
      ?? this.resolvedCompanyName
      ?? this.companyName
      ?? 'Organisation';
  }

  get canSwitchOrganisation(): boolean {
    return this.tenants.length > 1 || (this.isSuperAdmin && this.companies.length > 1);
  }

  ngOnInit(): void {
    this.loadMyCompany();
    this.auth.loadTenantMemberships().subscribe({
      next: tenants => this.tenants = tenants,
      error: () => this.tenants = [],
    });

    if (!this.isSuperAdmin) return;

    this.companyService.getCompanies().subscribe({
      next: response => {
        this.companies = (response.data ?? []).filter(company => company.isActive);
        const userId = this.auth.session()?.user.id;
        if (userId == null) return;

        const storedId = this.companyContext.getSelectedCompanyId(userId);
        const selected = this.companies.find(company => company.compId === storedId) ?? this.companies[0];
        this.selectedCompanyId = selected?.compId ?? null;
        if (selected && storedId !== selected.compId) {
          this.companyContext.setSelectedCompanyId(userId, selected.compId);
          window.location.reload();
        }
      },
    });
  }

  /**
   * Resolves the company name for the header. Super admins are scoped to whichever
   * company they have selected; everyone else to the compId on their session.
   */
  private loadMyCompany(): void {
    const user = this.auth.session()?.user;
    if (!user) return;

    const companyId = user.isSuperAdmin
      ? (this.companyContext.getSelectedCompanyId(user.id) ?? user.compId)
      : user.compId;

    this.resolvedCompanyId = companyId;
    this.companyService.getMyCompany(companyId).subscribe({
      next: response => {
        this.resolvedCompanyId = response.data?.compId ?? companyId;
        this.resolvedCompanyName = response.data?.name ?? '';
      },
      error: () => {
        this.resolvedCompanyName = '';
      },
    });
  }

  selectCompany(companyId: number | string | null): void {
    const userId = this.auth.session()?.user.id;
    const normalizedCompanyId = Number(companyId);
    if (userId == null || !Number.isInteger(normalizedCompanyId) || normalizedCompanyId <= 0) return;
    if (this.companyContext.getSelectedCompanyId(userId) === normalizedCompanyId) {
      this.organisationMenuOpen = false;
      return;
    }

    this.switchingOrganisation = true;
    this.organisationMenuOpen = false;
    this.companyContext.setSelectedCompanyId(userId, normalizedCompanyId);
    window.location.reload();
  }

  toggleOrganisationMenu(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.canSwitchOrganisation || this.switchingOrganisation) return;
    this.organisationMenuOpen = !this.organisationMenuOpen;
  }

  @HostListener('document:click')
  closeOrganisationMenu(): void {
    this.organisationMenuOpen = false;
  }

  @HostListener('document:keydown.escape')
  closeOrganisationMenuOnEscape(): void {
    this.organisationMenuOpen = false;
  }

  selectTenant(tenant: TenantMembership): void {
    if (this.tenantId(tenant) === this.auth.session()?.user.tenantId) {
      this.organisationMenuOpen = false;
      return;
    }

    this.switchingOrganisation = true;
    this.organisationMenuOpen = false;
    this.auth.switchTenant(tenant).subscribe({
      next: () => window.location.reload(),
      error: () => this.switchingOrganisation = false,
    });
  }

  tenantId(tenant: TenantMembership): string {
    return tenant.tenantId ?? tenant.tenant_id ?? tenant.id ?? '';
  }

  tenantName(tenant: TenantMembership): string {
    return tenant.displayName ?? tenant.tenantName ?? tenant.name ?? this.tenantId(tenant) ?? 'Organisation';
  }
}
